import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import type { BulkEditPayload, BulkStartResponse, BulkJobRehydration } from './bulk-edit.models';

export interface BulkSelectIdsResponse {
  ids: string[];
  total: number;
}

const REPEATABLE_QUERY_KEYS = new Set(['filter', 'search', 'sort']);

@Injectable({ providedIn: 'root' })
export class BulkEditService {
  private readonly http = inject(HttpClient);

  selectIds(resourcePath: string, filter: Record<string, unknown>): Observable<BulkSelectIdsResponse> {
    return this.http.post<BulkSelectIdsResponse>(`/api/v1/${resourcePath}/select-ids`, null, {
      params: buildQueryParams(filter),
    });
  }

  startPreview(resourcePath: string, payload: Omit<BulkEditPayload, 'dryRun'>): Observable<BulkStartResponse> {
    return this.http.post<BulkStartResponse>(`/api/v1/${resourcePath}/bulk-update`, { ...payload, dryRun: true });
  }

  startApply(resourcePath: string, payload: Omit<BulkEditPayload, 'dryRun'>): Observable<BulkStartResponse> {
    return this.http.post<BulkStartResponse>(`/api/v1/${resourcePath}/bulk-update`, payload);
  }

  cancel(resourcePath: string, jobId: string): Observable<void> {
    return this.http.delete<void>(`/api/v1/${resourcePath}/bulk-update/jobs/${jobId}`);
  }

  rehydrate(resourcePath: string, jobId: string): Observable<BulkJobRehydration> {
    return this.http.get<BulkJobRehydration>(`/api/v1/${resourcePath}/bulk-update/jobs/${jobId}`);
  }
}

function buildQueryParams(filter: Record<string, unknown>): HttpParams {
  let params = new HttpParams();
  for (const [key, value] of Object.entries(filter)) {
    if (isEmpty(value)) continue;
    params = Array.isArray(value) ? appendArrayParam(params, key, value) : appendScalarParam(params, key, value);
  }
  return params;
}

function appendArrayParam(params: HttpParams, key: string, values: unknown[]): HttpParams {
  if (!REPEATABLE_QUERY_KEYS.has(key)) return params.set(key, JSON.stringify(values));
  for (const entry of values) {
    if (!isEmpty(entry)) params = params.append(key, String(entry));
  }
  return params;
}

function appendScalarParam(params: HttpParams, key: string, value: unknown): HttpParams {
  return params.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
}

function isEmpty(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}
